// Named rather than namespaced: Vite 3's React plugin reparses a non-`.tsx` file that mentions a
// namespaced Fragment as plain JS, which chokes on the types. `wificard` is still on Vite 3.
import { Fragment } from 'react';
import * as React from 'react';

import { foldForSearch } from './filter';

/**
 * Adapts the collection API our combobox components inherited from react-stately — `<Item>` and
 * `<Section>` descriptor elements, optionally generated from an `items` array by a function child —
 * onto the flat `items` array Base UI's `Combobox` filters against.
 *
 * The walk runs once per `children`, not once per keystroke, and it precomputes everything a filter
 * pass or a render needs. Nothing here may be derived at match time.
 */

const DESCRIPTOR_ROLE = Symbol.for('atto.comboBox.descriptorRole');

type DescriptorRole = 'item' | 'section';

export interface ComboBoxItemProps<T = unknown> {
  children?: React.ReactNode;
  /**
   * Whether the option can be selected.
   */
  disabled?: boolean;
  /**
   * Identity of the option, matched against the combobox's `value`. Prefer React's `key`; this is
   * the fallback for a collection built without one.
   */
  id?: string;
  /**
   * Plain text for filtering and type-ahead. Falls back to the text content of `children`, so it is
   * only required when they render text no walk can reach.
   */
  textValue?: string;
  /**
   * Rendered in place of `children`, as react-stately's `Item` does.
   */
  title?: React.ReactNode;
  /**
   * Tooltip for this option's chip once selected. Honoured by `MultiComboBox`.
   */
  tooltipContents?: React.ReactNode;
  /**
   * Present so a section's function child can be typed against the item it receives.
   */
  value?: T;
}

export interface ComboBoxSectionProps<T = unknown> {
  children?: React.ReactNode | ((item: T) => React.ReactElement);
  /**
   * Options for a function child, in place of listing them statically.
   */
  items?: Iterable<T>;
  /**
   * Heading rendered above the section's options.
   */
  title?: React.ReactNode;
}

function descriptor(role: DescriptorRole) {
  function Descriptor(): never {
    throw new Error(
      `<ComboBox${role === 'item' ? 'Item' : 'Section'}> is not renderable on its own`,
    );
  }
  return Object.defineProperty(Descriptor, DESCRIPTOR_ROLE, { value: role });
}

/**
 * A single option. Inspected rather than rendered, so it takes no styling props — the combobox
 * renders `children` into its own option chrome.
 */
export const ComboBoxItem: <T>(props: ComboBoxItemProps<T>) => React.ReactElement =
  descriptor('item');

/**
 * A titled group of options. Inspected rather than rendered.
 */
export const ComboBoxSection: <T>(props: ComboBoxSectionProps<T>) => React.ReactElement =
  descriptor('section');

export interface ComboBoxRow {
  /**
   * Base UI's item value, and the option's React key.
   */
  value: string;
  /**
   * Alias of `value`. `renderSelected` callers read `option.key`.
   */
  key: string;
  /**
   * Base UI resolves the trigger's display from this, and the option renders it.
   */
  label: React.ReactNode;
  textValue: string;
  /**
   * Secondary line under the label. Searchable, but never mirrored into the trigger.
   */
  description: string | undefined;
  /**
   * Every searchable field of the row, folded, computed once per collection.
   */
  haystack: string;
  disabled: boolean;
  tooltipContents: React.ReactNode;
}

export interface ComboBoxRowGroup {
  key: string;
  label: React.ReactNode;
  /**
   * Named `items` because Base UI recognises a group by that field.
   */
  items: ComboBoxRow[];
}

export interface ComboBoxCollection {
  /**
   * Groups when the children were sectioned, otherwise `undefined`. Base UI switches filtering and
   * rendering modes on the shape of `items`, so the two cases stay separate.
   */
  groups: ComboBoxRowGroup[] | undefined;
  /**
   * Every row, group membership flattened away. Lookups and the ungrouped list read this.
   */
  rows: ComboBoxRow[];
  byValue: Map<string, ComboBoxRow>;
}

const EMPTY_COLLECTION: ComboBoxCollection = {
  groups: undefined,
  rows: [],
  byValue: new Map(),
};

function finish(rows: ComboBoxRow[], groups: ComboBoxRowGroup[] | undefined): ComboBoxCollection {
  return { groups, rows, byValue: new Map(rows.map((row) => [row.value, row])) };
}

/**
 * A query is trimmed before it's matched, so this only has to be something a query can't contain —
 * otherwise a match would be able to straddle the boundary between two of a row's fields.
 */
const HAYSTACK_SEPARATOR = '\u0000';

/** Every searchable field of a row, folded together. */
function haystackOf(...parts: (string | undefined)[]): string {
  return foldForSearch(parts.filter(Boolean).join(HAYSTACK_SEPARATOR));
}

function roleOf(type: unknown): DescriptorRole | undefined {
  if (typeof type !== 'function') return undefined;
  if (DESCRIPTOR_ROLE in type) {
    const role = Reflect.get(type, DESCRIPTOR_ROLE);
    return role === 'item' || role === 'section' ? role : undefined;
  }
  return undefined;
}

/** Text content of a node, for a row that named no `textValue`. */
function nodeText(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join(' ');
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return nodeText(node.props.children);
  }
  return '';
}

/** Either descriptor's props as the walk sees them, once `isValidElement` has widened the element. */
interface WalkProps {
  children?: React.ReactNode | ((item: unknown) => React.ReactElement);
  disabled?: boolean;
  id?: string;
  items?: Iterable<unknown>;
  textValue?: string;
  title?: React.ReactNode;
  tooltipContents?: React.ReactNode;
}

function expand<T>(
  children: React.ReactNode | ((item: T) => React.ReactElement),
  items: Iterable<T> | undefined,
): React.ReactNode {
  if (typeof children !== 'function') return children;
  if (!items) return null;
  return Array.from(items, children);
}

/**
 * Every descriptor in `children`, in author order, with fragments flattened away — a helper that
 * groups options is a fragment as far as the walk is concerned, and its contents still belong to the
 * enclosing list.
 */
function forEachDescriptor(
  children: React.ReactNode,
  visit: (element: React.ReactElement<WalkProps>, index: number) => void,
): void {
  let index = 0;

  const walk = (nodes: React.ReactNode) => {
    React.Children.forEach(nodes, (child) => {
      if (!React.isValidElement<WalkProps>(child)) return;
      if (child.type === Fragment) {
        const { children: nested } = child.props;
        if (typeof nested !== 'function') walk(nested);
        return;
      }
      visit(child, index);
      index += 1;
    });
  };

  walk(children);
}

function rowFrom(element: React.ReactElement<WalkProps>, index: number): ComboBoxRow {
  const { children, disabled, id, textValue, title, tooltipContents } = element.props;
  const value = element.key ?? id ?? String(index);
  const label = title ?? (typeof children === 'function' ? null : children);
  const text = textValue ?? nodeText(label);

  return {
    value,
    key: value,
    label,
    textValue: text,
    description: undefined,
    haystack: haystackOf(text, value),
    disabled: disabled ?? false,
    tooltipContents,
  };
}

/**
 * Builds a collection from the descriptor tree. Exported for tests; components use
 * `useComboBoxCollection`.
 */
export function buildComboBoxCollection<T>(
  children: React.ReactNode | ((item: T) => React.ReactElement),
  items?: Iterable<T>,
): ComboBoxCollection {
  const rows: ComboBoxRow[] = [];
  const groups: ComboBoxRowGroup[] = [];
  let sawSection = false;
  // Options sitting alongside sections rather than inside one. Collected into a headingless group so
  // grouped mode keeps them, in the order they were authored.
  let looseGroup: ComboBoxRowGroup | undefined;

  forEachDescriptor(expand(children, items), (child, index) => {
    const role = roleOf(child.type);

    if (role === 'item') {
      const row = rowFrom(child, index);
      rows.push(row);
      if (!looseGroup) {
        looseGroup = { key: `loose-${index}`, label: undefined, items: [] };
        groups.push(looseGroup);
      }
      looseGroup.items.push(row);
      return;
    }
    if (role !== 'section') return;

    const sectionRows: ComboBoxRow[] = [];
    forEachDescriptor(expand(child.props.children, child.props.items), (grandchild, grandIndex) => {
      if (roleOf(grandchild.type) !== 'item') return;
      sectionRows.push(rowFrom(grandchild, grandIndex));
    });
    if (!sectionRows.length) return;

    sawSection = true;
    looseGroup = undefined;
    rows.push(...sectionRows);
    groups.push({
      key: child.key ?? `section-${index}`,
      label: child.props.title,
      items: sectionRows,
    });
  });

  if (!rows.length) return EMPTY_COLLECTION;

  return finish(rows, sawSection ? groups : undefined);
}

/**
 * Base UI's `itemToStringLabel`. It reaches for `item.label` by default, which for us is a node
 * rather than a string, so the trigger's type-ahead needs pointing at `textValue` instead. Called
 * with a bare value as well as a row, hence the guard.
 */
export function comboBoxRowLabel(item: unknown): string {
  if (typeof item === 'object' && item !== null && 'textValue' in item) {
    const { textValue } = item;
    if (typeof textValue === 'string') return textValue;
  }
  return String(item);
}

/**
 * Base UI compares `items` entries — rows — against the selected value, which components keep as a
 * plain string. Left to `Object.is` the selected option never resolves an index, so opening the
 * popup highlights and scrolls to nothing.
 */
export function comboBoxIsItemEqualToValue(item: unknown, value: unknown): boolean {
  if (typeof item === 'object' && item !== null && 'value' in item) return item.value === value;
  return Object.is(item, value);
}

export function useComboBoxCollection<T>(
  children: React.ReactNode | ((item: T) => React.ReactElement),
  items?: Iterable<T>,
): ComboBoxCollection {
  return React.useMemo(() => buildComboBoxCollection(children, items), [children, items]);
}

/** Folds a list of keys into a memo key, joined on the one character a key can't contain. */
const KEY_SEPARATOR = '\u0000';

/**
 * Stable across the fresh arrays and sets consumers build inline, so a parent's render doesn't
 * invalidate every option under it.
 */
export function useValueList(keys: Iterable<string | number> | undefined): string[] | undefined {
  const signature = keys == null ? undefined : Array.from(keys, String).join(KEY_SEPARATOR);
  return React.useMemo(() => {
    if (signature == null) return undefined;
    return signature === '' ? [] : signature.split(KEY_SEPARATOR);
  }, [signature]);
}

/** As `useValueList`, for membership tests. */
export function useValueSet(keys: Iterable<string | number> | undefined): ReadonlySet<string> {
  const list = useValueList(keys);
  return React.useMemo(() => new Set(list), [list]);
}

/**
 * Base UI's own `items` shape: a record of value → label, a flat array of options, or an array of
 * `{ label?, items }` groups.
 */
export type ComboBoxItemsProp = Record<string, React.ReactNode> | ReadonlyArray<unknown>;

interface ItemShape {
  value: unknown;
  label?: React.ReactNode;
  description?: string;
  disabled?: boolean;
}

interface GroupShape {
  items: ReadonlyArray<unknown>;
  label?: React.ReactNode;
}

function isItemShape(item: unknown): item is ItemShape {
  return item != null && typeof item === 'object' && 'value' in item;
}

function isGroupShape(item: unknown): item is GroupShape {
  return item != null && typeof item === 'object' && 'items' in item && Array.isArray(item.items);
}

function itemValue(item: unknown): string {
  return String(isItemShape(item) ? item.value : item);
}

function rowFromItem(item: unknown): ComboBoxRow {
  if (!isItemShape(item)) {
    const text = String(item);
    return {
      value: text,
      key: text,
      label: text,
      textValue: text,
      description: undefined,
      haystack: foldForSearch(text),
      disabled: false,
      tooltipContents: undefined,
    };
  }

  const value = String(item.value);
  const label = item.label ?? value;
  const textValue = typeof label === 'string' ? label : nodeText(label);

  return {
    value,
    key: value,
    label,
    textValue,
    description: item.description,
    haystack: haystackOf(textValue, value, item.description),
    disabled: item.disabled ?? false,
    tooltipContents: undefined,
  };
}

/**
 * Content-derived so a group keeps its identity across reorders, but O(1) rather than a join over
 * every value — with thousands of rows in one group that key would be the size of the list itself,
 * rebuilt and string-compared on every reconcile.
 */
function groupKey(group: GroupShape, index: number): string {
  if (typeof group.label === 'string' && group.label !== '') return `label:${group.label}`;
  if (group.items.length === 0) return `group:${index}:empty`;
  const first = itemValue(group.items[0]);
  const last = itemValue(group.items[group.items.length - 1]);
  return `group:${index}:${group.items.length}:${first}:${last}`;
}

/**
 * Builds a collection from Base UI's `items` shape, for components that take options as data rather
 * than as descriptor children.
 */
export function buildCollectionFromItems(items: ComboBoxItemsProp): ComboBoxCollection {
  if (!Array.isArray(items)) {
    const rows = Object.entries(items).map(([value, label]) => rowFromItem({ value, label }));
    return rows.length ? finish(rows, undefined) : EMPTY_COLLECTION;
  }
  if (!items.length) return EMPTY_COLLECTION;

  if (isGroupShape(items[0])) {
    const rows: ComboBoxRow[] = [];
    const groups = items.filter(isGroupShape).map((group, index) => {
      const groupRows = group.items.map(rowFromItem);
      rows.push(...groupRows);
      return { key: groupKey(group, index), label: group.label, items: groupRows };
    });
    return rows.length ? finish(rows, groups) : EMPTY_COLLECTION;
  }

  return finish(items.map(rowFromItem), undefined);
}

export function useCollectionFromItems(items: ComboBoxItemsProp): ComboBoxCollection {
  return React.useMemo(() => buildCollectionFromItems(items), [items]);
}
